'use client';

import React from 'react';
import clsx from 'clsx';
import { EmblaOptionsType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import emblaClassNames from 'embla-carousel-class-names';
import useEmblaCarousel from 'embla-carousel-react';
import dynamic from 'next/dynamic';

import {
  DotButton,
  NextButton,
  PrevButton,
  useDotButton,
  usePrevNextButtons,
} from './cms-carousel-controls';
import styles from './cms-carousel.module.scss';

const Gallery = dynamic(() =>
  import('react-photoswipe-gallery').then((m) => ({ default: m.Gallery }))
);
const Item = dynamic(() =>
  import('react-photoswipe-gallery').then((m) => ({ default: m.Item }))
);

type MediaItem = {
  id: string | number;
  content: React.ReactNode;
  original?: string;
  width?: number;
  height?: number;
  alt?: string | null;
  caption?: string | null;
};

type CarouselProps = {
  slides: MediaItem[];
  options?: EmblaOptionsType;
  slideClassName?: string;
  slideContainerClassName?: string;
  slideWrapperClassName?: string;
  carouselClassName?: string;
  enableGallery?: boolean;
  enableDots?: boolean;
  autoplayOptions?: {
    delay?: number;
    stopOnInteraction?: boolean;
    stopOnMouseEnter?: boolean;
    rootNode?: (emblaRoot: HTMLElement) => HTMLElement;
  };
};

const CarouselContent: React.FC<{
  slide: MediaItem;
  slideClassName?: string;
  enableGallery?: boolean;
}> = ({ slide, slideClassName, enableGallery }) => {
  if (enableGallery && slide.original) {
    return (
      <Item
        original={slide.original}
        width={slide.width || 0}
        height={slide.height || 0}
        alt={slide.alt || ''}
        caption={slide.caption || ''}
      >
        {({ ref, open }) => (
          <div
            ref={ref}
            onClick={(e) => {
              e.stopPropagation();
              open(e);
            }}
            className={clsx(
              'slide-content transition-opacity hover:opacity-85',
              slideClassName
            )}
          >
            {slide.content}
          </div>
        )}
      </Item>
    );
  }

  return (
    <div className={clsx('slide-content', slideClassName)}>{slide.content}</div>
  );
};

const CmsCarousel: React.FC<CarouselProps> = (props) => {
  const {
    slides,
    options,
    slideClassName,
    slideContainerClassName,
    slideWrapperClassName,
    carouselClassName,
    enableGallery = false,
    enableDots = false,
    autoplayOptions,
  } = props;

  const autoplayPlugin = autoplayOptions
    ? [
        Autoplay({
          delay: autoplayOptions.delay || 4000,
          stopOnInteraction: autoplayOptions.stopOnInteraction ?? true,
          stopOnMouseEnter: autoplayOptions.stopOnMouseEnter ?? true,
          rootNode: autoplayOptions.rootNode,
        }),
      ]
    : [];

  const [emblaRef, emblaApi] = useEmblaCarousel(options, [
    emblaClassNames(),
    ...autoplayPlugin,
  ]);

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  const CarouselSection = (
    <section
      className={clsx('embla relative', styles.embla, carouselClassName)}
    >
      <div
        className="embla__viewport mx-auto overflow-hidden pl-4 sm:pl-0"
        ref={emblaRef}
      >
        <div className={clsx('embla__container', slideContainerClassName)}>
          {slides.map((slide) => (
            <div
              className={clsx(
                'embla__slide',
                enableGallery ? 'cursor-pointer' : '',
                slideWrapperClassName
              )}
              key={slide.id}
            >
              <CarouselContent
                slide={slide}
                slideClassName={slideClassName}
                enableGallery={enableGallery}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="embla__controls hidden sm:block">
        <div className="embla__buttons">
          <PrevButton
            type="button"
            aria-label="Previous"
            value="previous"
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
          />
          <NextButton
            type="button"
            aria-label="Next"
            value="next"
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
          />
        </div>
      </div>

      {enableDots && (
        <div className="embla__dots align-center mt-2.5 flex justify-center gap-3">
          {scrollSnaps.map((_, index) => (
            <DotButton
              aria-label={`Go to slide ${index + 1}`}
              value={index + 1}
              type="button"
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={'embla__dot block h-2.5 w-2.5 rounded-lg p-0'.concat(
                index === selectedIndex
                  ? ' embla__dot--selected bg-gray-6'
                  : ' bg-gray-4'
              )}
            />
          ))}
        </div>
      )}
    </section>
  );

  if (enableGallery) {
    return (
      <Gallery
        options={{
          showHideAnimationType: 'fade',
          padding: { top: 20, bottom: 20, left: 60, right: 60 },
        }}
      >
        {CarouselSection}
      </Gallery>
    );
  }

  return CarouselSection;
};

export default CmsCarousel;
